import isEqual from 'lodash.isequal';
import { useEffect, useRef } from 'react';
import { useWatch, Path, FieldValues, PathValue, Control, useFormContext } from 'react-hook-form';
import { useFieldSchema } from './useFieldSchema';


export function useWatchCallback<T extends FieldValues, TPath extends Path<T>>(
	control: Control<T>,
	linkTo: TPath,
	onWatch?: (value: PathValue<T, TPath>) => void
) {

	const watchedRawValue = useWatch({ control, name: linkTo });
	const prevRawValueRef = useRef(watchedRawValue);

	const schema = useFieldSchema<T>(linkTo);
	const { trigger, clearErrors, setError } = useFormContext<T>();

	useEffect(() => {

		// Si no tengo nada que escuchar, no voy validando errores ni hago nada
		if (onWatch === undefined)
			return;


		let isMounted = true;
		// Sólo ejecute el onWatch cuando el valor es válido. Si tengo un esquema y no es válido
		// lo que hago es pintar el error.
		const onWatchCallback = async () => {

			clearErrors(linkTo);

			const isValid = await trigger(linkTo);
			if (isMounted && isValid) {
				const result = schema.safeParse(watchedRawValue);
				if (!result.success) {
					setError(linkTo, result.error);
					return;
				}

				onWatch?.(result.data);
			}
		};


		if (!isEqual(prevRawValueRef.current, watchedRawValue)) {
			prevRawValueRef.current = watchedRawValue;
			void onWatchCallback();
		}

		return () => {
			isMounted = false;
		};

	}, [watchedRawValue, onWatch, clearErrors, trigger, linkTo, schema, setError]);
}